import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Chart } from "primereact/chart";
import { Calendar } from "primereact/calendar";
import { EssCard } from "./components/helper";
import { toast } from "react-toastify";
import { supportApi } from "../../services";
import moment from "moment";

const REPORT_TYPES = ["Monthly Summary", "SLA Compliance", "CSAT Feedback"].map(
  (v) => ({ label: v, value: v }),
);
const FREQ_OPT = ["Daily", "Weekly", "Monthly"].map((v) => ({
  label: v,
  value: v,
}));

const chartOpts = {
  plugins: { legend: { display: false } },
  scales: { y: { grid: { color: "#f0f0f0" }, beginAtZero: true } },
  maintainAspectRatio: false,
};

const REPORT_CARDS = [
  {
    key: "monthly",
    icon: "📊",
    title: "Monthly Summary Report",
    desc: "All tickets, resolution stats and agent performance for a selected month",
    btnLabel: "Download Excel",
    btnColor: "#2563a8",
    format: "excel",
  },
  {
    key: "sla",
    icon: "⏱️",
    title: "SLA Compliance Report",
    desc: "Tickets breached, near-breach and on-time delivery analysis",
    btnLabel: "Download PDF",
    btnColor: "#fb8c00",
    format: "pdf",
  },
  {
    key: "csat",
    icon: "⭐",
    title: "CSAT / Feedback Report",
    desc: "Customer satisfaction scores, agent ratings and comments from users",
    btnLabel: "Download Excel",
    btnColor: "#22a06b",
    format: "excel",
  },
];

const Reports = ({ departmentId: propDeptId }) => {
  const currentYear = new Date().getFullYear();
  const defaultFrom = new Date(currentYear, 0, 1);
  const defaultTo = new Date();

  const [dateRange, setDateRange] = useState({ from: defaultFrom, to: defaultTo });
  const [trendData, setTrendData] = useState({ labels: [], datasets: [] });
  const [resData, setResData] = useState({ labels: [], datasets: [] });
  const [trendLabel, setTrendLabel] = useState("");
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [schedule, setSchedule] = useState({ reportType: null, frequency: null, recipients: "" });
  const [scheduling, setScheduling] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);

  useEffect(() => {
    supportApi.getDepartments()
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        if (list.length > 0) {
          setDepartments([
            { label: 'All Departments', value: null },
            ...list.map((d) => ({ label: d.title, value: d.id })),
          ]);
        }
      })
      .catch(() => {});
  }, []);

  // Sync with parent dept filter
  useEffect(() => {
    if (propDeptId !== undefined) setSelectedDept(propDeptId);
  }, [propDeptId]);

  const fetchCharts = async (from, to, deptId) => {
    setLoadingCharts(true);
    const fromStr = from ? moment(from).format("YYYY-MM-DD") : "";
    const toStr = to ? moment(to).format("YYYY-MM-DD") : "";
    const label =
      from && to
        ? `(${moment(from).format("DD MMM YYYY")} - ${moment(to).format("DD MMM YYYY")})`
        : `(${currentYear})`;
    setTrendLabel(label);
    try {
      const [trendRes, slaRes] = await Promise.all([
        supportApi.getMonthlyTrend({
          year: from ? moment(from).year() : currentYear,
          departmentId: deptId ?? "",
        }),
        supportApi.getSlaComplianceReport({
          fromDate: fromStr,
          toDate: toStr,
          departmentId: deptId ?? "",
        }),
      ]);
      const trend = trendRes?.data?.data ?? trendRes?.data ?? {};
      setTrendData({
        labels: trend.labels ?? [],
        datasets: [
          {
            label: "Tickets",
            data: trend.data ?? [],
            backgroundColor: ["#3b82c4", "#2563a8", "#1e3a5f"],
            borderRadius: 6,
          },
        ],
      });
      const sla = slaRes?.data?.data ?? slaRes?.data ?? {};
      setResData({
        labels: sla.labels ?? ["Critical", "High", "Medium", "Low"],
        datasets: [
          {
            label: "Avg Hrs",
            data: sla.avgHrs ?? sla.data ?? [],
            backgroundColor: ["#e53935", "#fb8c00", "#2563a8", "#22a06b"],
            borderRadius: 6,
          },
        ],
      });
    } catch {
      toast.error("Failed to load chart data");
    } finally {
      setLoadingCharts(false);
    }
  };

  // Re-fetch whenever department changes
  useEffect(() => {
    fetchCharts(dateRange.from, dateRange.to, selectedDept);
  }, [selectedDept]); // eslint-disable-line

  // Initial load
  useEffect(() => {
    fetchCharts(defaultFrom, defaultTo, null);
  }, []); // eslint-disable-line

  const onApplyFilter = () => {
    if (!dateRange.from || !dateRange.to) {
      toast.warn("Please select both From and To dates");
      return;
    }
    fetchCharts(dateRange.from, dateRange.to, selectedDept);
  };

  const onDownload = async (reportType, format) => {
    setDownloading(reportType);
    try {
      const fromStr = dateRange.from
        ? moment(dateRange.from).startOf("month").format("YYYY-MM-DD")
        : "";
      const toStr = dateRange.to
        ? moment(dateRange.to).endOf("month").format("YYYY-MM-DD")
        : "";
      const res = await supportApi.downloadReport({
        reportType,
        fromDate: fromStr,
        toDate: toStr,
        format,
        departmentId: selectedDept ?? "",
      });
      const mimeType =
        format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportType.replace(/ /g, "_")}_${fromStr}_${toStr}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${reportType} downloaded`);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading("");
    }
  };

  const onSchedule = async () => {
    if (
      !schedule.reportType ||
      !schedule.frequency ||
      !schedule.recipients.trim()
    ) {
      toast.warn("Please fill all schedule fields");
      return;
    }
    setScheduling(true);
    try {
      await supportApi.scheduleReport({
        reportType: schedule.reportType,
        frequency: schedule.frequency,
        recipients: schedule.recipients
          .split(",")
          .map((r) => r.trim())
          .filter(Boolean),
      });
      toast.success("Report scheduled successfully");
      setSchedule({ reportType: null, frequency: null, recipients: "" });
    } catch {
      toast.error("Failed to schedule report");
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div>
      {/* Department selector — hidden when parent controls it */}
      {departments.length > 1 && propDeptId === undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>Department:</label>
          <Dropdown
            value={selectedDept}
            options={departments}
            onChange={(e) => {
              setSelectedDept(e.value);
              fetchCharts(dateRange.from, dateRange.to);
            }}
            style={{ minWidth: 200 }}
          />
        </div>
      )}
      {/* Date Range Filter */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <label
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              display: "block",
              marginBottom: 4,
              color: "var(--navy)",
            }}
          >
            From
          </label>
          <Calendar
            value={dateRange.from}
            onChange={(e) => setDateRange((p) => ({ ...p, from: e.value }))}
            dateFormat="dd/mm/yy"
            showIcon
            style={{ width: 160 }}
          />
        </div>
        <div>
          <label
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              display: "block",
              marginBottom: 4,
              color: "var(--navy)",
            }}
          >
            To
          </label>
          <Calendar
            value={dateRange.to}
            onChange={(e) => setDateRange((p) => ({ ...p, to: e.value }))}
            dateFormat="dd/mm/yy"
            showIcon
            style={{ width: 160 }}
          />
        </div>
        <Button
          label="Apply"
          icon="pi pi-filter"
          loading={loadingCharts}
          style={{ background: "var(--navy)", border: "none", height: 38 }}
          onClick={onApplyFilter}
        />
      </div>

      {/* Charts Row */}
      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="mini-chart">
          <div className="mini-chart-title">
            MONTHLY TICKET TREND {trendLabel}
          </div>
          <div style={{ height: 220 }}>
            <Chart type="bar" data={trendData} options={chartOpts} />
          </div>
        </div>
        <div className="mini-chart">
          <div className="mini-chart-title">
            AVG RESOLUTION TIME BY PRIORITY (HOURS)
          </div>
          <div style={{ height: 220 }}>
            <Chart type="bar" data={resData} options={chartOpts} />
          </div>
        </div>
      </div>

      {/* Download Cards */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        {REPORT_CARDS.map(
          ({ key, icon, title, desc, btnLabel, btnColor, format }) => (
            <div className="export-card" key={key}>
              <div className="export-icon">{icon}</div>
              <div className="export-title">{title}</div>
              <div className="export-desc">{desc}</div>
              <Button
                label={btnLabel}
                icon="pi pi-download"
                loading={downloading === title}
                style={{
                  background: btnColor,
                  border: "none",
                  width: "100%",
                  justifyContent: "center",
                }}
                onClick={() => onDownload(title, format)}
              />
            </div>
          ),
        )}
      </div>

      {/* Schedule Section */}
      <EssCard title="Schedule Automated Reports" icon="pi-calendar">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr auto",
            gap: 12,
            alignItems: "flex-end",
          }}
        >
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Report Type
            </label>
            <Dropdown
              value={schedule.reportType}
              options={REPORT_TYPES}
              onChange={(e) =>
                setSchedule((p) => ({ ...p, reportType: e.value }))
              }
              placeholder="Select..."
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Frequency
            </label>
            <Dropdown
              value={schedule.frequency}
              options={FREQ_OPT}
              onChange={(e) =>
                setSchedule((p) => ({ ...p, frequency: e.value }))
              }
              placeholder="Select..."
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Recipients
            </label>
            <InputText
              value={schedule.recipients}
              onChange={(e) =>
                setSchedule((p) => ({ ...p, recipients: e.target.value }))
              }
              placeholder="email@espire.com"
              style={{ width: "100%" }}
            />
          </div>
          <Button
            label="Schedule"
            icon="pi pi-clock"
            loading={scheduling}
            style={{ background: "var(--navy)", border: "none" }}
            onClick={onSchedule}
          />
        </div>
      </EssCard>
    </div>
  );
};

export default Reports;
